import { useCallback, useState } from "react";

import { useNorthbeamQuery } from "@hooks/http";
import {
  ConnectionType,
  type FetchSanitizedConnectionsByType,
  type FetchSanitizedConnectionsByTypeVariables,
} from "@north-beam/nb-client-side";
import { useUpsertConnection } from "@pages/settings/connection-status-manager";
import { FETCH_SANITIZED_CONNECTIONS_BY_TYPE } from "@pages/settings/queries";
import { PrimaryButton } from "@shared/buttons";

import { Platform } from "../constants";

import { SetupSection } from "./components/setup-section";
import { Code, OrderedList, SubSubSectionHeader } from "./components/styled";
import { VideoContent } from "./components/video-content";
import { type PlatformDetails } from "./platform-details";

const PLATFORM = Platform.KLAVIYO;

export const KlaviyoDetails: PlatformDetails = {
  northbeamDocUrl:
    "https://docs.northbeam.io/docs/setting-up-klaviyo-for-northbeam",
  officialDocUrl: "",
  additionalDetails: [
    {
      title: "What does the native integration do?",
      content: `Our integration with Klaviyo imports all Klaviyo events associated with
      the "new subscriber" tag. You'll see these reporting under the metric
      called "Email Sign Ups".`,
    },
    {
      title: "Why doesn’t the Klaviyo integration capture ad spend",
      content: `Klaviyo’s integration does not provide spend data.
        Instead spend is tracked through Northbeam’s Spend API.
        You’ll need to configure the Spend API in next step`,
    },
  ],
};

const CONNECTION_TYPE = ConnectionType.integration_klaviyo;

export const KlaviyoConnectionSetup = () => {
  const { data, loading: fetchLoading } = useNorthbeamQuery<
    FetchSanitizedConnectionsByType,
    FetchSanitizedConnectionsByTypeVariables
  >(FETCH_SANITIZED_CONNECTIONS_BY_TYPE, {
    variables: { type: CONNECTION_TYPE },
  });

  const connection = data?.me.sanitizedConnections[0];

  const { upsertConnection, isLoading } = useUpsertConnection(
    connection?.id || null,
    CONNECTION_TYPE,
  );

  const [publicKey, setPublicKey] = useState<string | undefined>(
    connection?.data.public_key,
  );
  const [privateKey, setPrivateKey] = useState<string | undefined>(
    connection?.data.public_key,
  );
  const onConnectClicked = useCallback(async () => {
    await upsertConnection({
      public_key: publicKey,
      private_key: privateKey,
    });
  }, [publicKey, privateKey, upsertConnection]);

  return fetchLoading ? (
    <div>Loading</div>
  ) : (
    <div className="mb-3">
      <div className="row">
        <div className="col-auto">
          <label className="sr-only" htmlFor="klaviyoPublicKey">
            Public Key
          </label>
          <div className="input-group">
            <input
              disabled={isLoading}
              type="text"
              className="form-control"
              value={publicKey}
              placeholder="Public key..."
              id="klaviyoPublicKey"
              onChange={(event) => setPublicKey(event.target.value)}
            />
          </div>
        </div>
        <div className="col-auto">
          <label className="sr-only" htmlFor="klaviyoPrivateKey">
            Private Key
          </label>
          <div className="input-group">
            <input
              disabled={isLoading}
              type="text"
              className="form-control"
              value={privateKey}
              placeholder="Private key..."
              id="klaviyoPrivateKey"
              onChange={(event) => setPrivateKey(event.target.value)}
            />
          </div>
        </div>
        <div className="col-auto">
          <PrimaryButton
            loading={isLoading}
            disabled={!publicKey || !privateKey}
            onClick={onConnectClicked}
          >
            {isLoading ? "Please Wait..." : "CONNECT"}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};

export const KlaviyoSetup = ({
  connectionContent,
}: {
  connectionContent: React.ReactNode;
}) => {
  return (
    <>
      <SetupSection
        title="Submit your public and private Klaviyo keys to establish
          Northbeam’s Klaviyo integration"
      >
        {connectionContent}
      </SetupSection>
      <SetupSection title="Update UTMs for new campaigns and flows (takes ~1 minute)">
        <VideoContent
          title="Klaviyo UTMs for New Campaigns"
          videoUrl="https://www.loom.com/embed/62739ccc2bfe4989a8e0a4b14ffca5b8"
          platform={PLATFORM}
        />
        <SubSubSectionHeader>
          Make sure you’ve followed these steps:
        </SubSubSectionHeader>
        <OrderedList variant="upper-alpha">
          <>Login to Klaviyo</>
          <>Click on ‘Account’</>
          <>Click on ‘UTM Tracking’</>
          <>Click ‘Add Custom UTM Parameters’</>
          <>
            Fill in the following information for each field
            <OrderedList indent={1}>
              <>
                UTM Parameter:
                <Code className="mx-2">nb_klid</Code>
                <i>
                  Please do NOT copy and paste that nb_klid slug, for some
                  reason, that causes a bug. Manually type it out.
                </i>
              </>
              <>
                Campaign Email Value:
                <Code className="mx-2">Klaviyo Profile ID</Code>
              </>
              <>
                Flow Email Value:
                <Code className="mx-2">Klaviyo Profile ID</Code>
              </>
              <>Turn on ‘Automatically add UTM to parameters to links’</>
              <>Click ‘Update Tracking Settings’</>
            </OrderedList>
          </>
        </OrderedList>
      </SetupSection>
      <SetupSection
        title="Update UTMs for historical Flows and automations that are still
          active (takes ~10 - 15 minute)"
      >
        <VideoContent
          title="Klaviyo UTMs for Historical Flows"
          videoUrl=""
          platform={PLATFORM}
        />
        <SubSubSectionHeader>
          Make sure you’ve followed these steps:
        </SubSubSectionHeader>
        <OrderedList variant="upper-alpha">
          <>
            Inside of Klaviyo, navigate to your automation and click Edit Flow.
            Duplicate the existing Flow if necessary.,
          </>
          <>
            Perform the following tasks:
            <OrderedList indent={1}>
              <>Select your email</>
              <>Click ‘UTM Tracking’</>
              <>Click ‘Configure’ under ‘UTM Tracking’</>
              <>Toggle ‘No’</>
              <>Click ‘Add a Custom UTM Parameter’ </>
              <>
                Type in
                <Code className="mx-2">nb_klid</Code>
              </>
              <>
                Value select
                <Code className="mx-2">Klaviyo profile</Code>
              </>
            </OrderedList>
          </>
          <>Click on ‘Save’</>
          <>Repeat for all your emails in this Flow</>
        </OrderedList>
        <div className="flex gap-2">
          <b>Note:</b>In some cases, we’ve seen that
          <Code>nb_klid</Code>
          auto-populates in the Flows. If this is the case for you, please skip
          this step.
        </div>
      </SetupSection>
    </>
  );
};
